import React, { useEffect, useState } from 'react';

/**
 * Sticky reading-progress bar that fills as the user scrolls down the page.
 * Uses requestAnimationFrame for smooth updates and avoids re-renders for tiny deltas.
 */
const ReadingProgressBar = ({ targetRef }) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      const el = targetRef?.current;
      const docHeight = el
        ? el.getBoundingClientRect().height
        : document.documentElement.scrollHeight - window.innerHeight;
      const offsetTop = el ? el.getBoundingClientRect().top + window.scrollY : 0;
      const scrolled = Math.max(0, window.scrollY - offsetTop);
      const total = Math.max(1, docHeight - window.innerHeight);
      const next = Math.min(100, Math.max(0, (scrolled / total) * 100));
      setProgress((prev) => (Math.abs(prev - next) > 0.5 ? next : prev));
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [targetRef]);

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[60] h-[3px] pointer-events-none"
      data-testid="reading-progress-bar"
      aria-hidden="true"
    >
      <div
        className="h-full bg-[#83BF42] transition-[width] duration-150 ease-out"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

export default ReadingProgressBar;
